// client/src/components/auth/MyAccountPage.js
import React, { useState, useEffect, useCallback } from 'react';
import useAlert from '../../hooks/useAlert';
import { useAuthMutations, useMe } from '../../hooks/useAuth';
import { useMutateUsers } from '../../hooks/useUsers';
import {
    User as UserIcon, Loader2, Save, MapPin, Settings, Lock, AlertCircle, UploadCloud, Camera, X
} from 'lucide-react';
import PageHeader from '../layout/PageHeader';
import FabSaveButton from '../common/FabSaveButton';

import ProfileSection from '../plataforma/users/sections/ProfileSection';
import PasswordSection from '../plataforma/users/sections/PasswordSection';
import UnitsSection from '../unidades/sections/UnitsSection';
import ServicesSection from '../profissionais/sections/ServicesSection';

const MyAccountPage = () => {
    const { setAlert } = useAlert();
    
    // Hook useMe para pegar dados frescos do usuário logado
    const { user, isLoading: authLoading, isError, error, refetch } = useMe();
    
    // Mutations de Auth
    const authMutations = useAuthMutations(setAlert);
    const { updateMyDetails, updateMyPassword } = authMutations;
    
    // ✅ CORREÇÃO: Mutations de usuário
    const userMutations = useMutateUsers();
    const { 
        updateMyPhoto, 
        removeMyPhoto, 
        isUploadingMyPhoto, 
        isRemovingMyPhoto 
    } = userMutations;

    // Estado local da página
    const [activeTab, setActiveTab] = useState('perfil');
    const [isSaving, setIsSaving] = useState(false);
    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [uploadError, setUploadError] = useState(null);
    
    // Estado do Formulário
    const [formData, setFormData] = useState({});
    
    // Estado para Vínculos
    const [selectedUnidades, setSelectedUnidades] = useState([]);
    const [selectedServicos, setSelectedServicos] = useState([]);
    const [comissoesConfig, setComissoesConfig] = useState({ unidades: {}, servicos: {} });

    // 1. Carregar dados do usuário no form
    useEffect(() => {
        if (user) {
            setFormData({
                ...user,
                password: '',
                confirmPassword: '',
                currentPassword: '',
                profissional: user.profissional || { isProfissional: false }
            });
            
            // ✅ Configurar preview da foto se existir
            if (user.photoUrl || user.photo) {
                setPhotoPreview(user.photoUrl || user.photo);
            }
        }
    }, [user]);

    // 2. Tratamento de Erros de carregamento
    useEffect(() => { 
        if (isError) { 
            console.error('❌ [MyAccountPage] Erro ao carregar dados:', error);
            setAlert(error?.response?.data?.error || 'Erro ao carregar dados do usuário', 'danger'); 
        } 
    }, [isError, error, setAlert]);

    // Cleanup do preview da foto
    useEffect(() => {
        return () => {
            if (photoPreview && photoPreview.startsWith('blob:')) {
                URL.revokeObjectURL(photoPreview);
            }
        };
    }, [photoPreview]);

    // --- HANDLERS PARA FOTO ---

    // ✅ Handle para selecionar arquivo de foto
    const handlePhotoFileChange = (event) => {
        const selectedFile = event.target.files[0];
        if (!selectedFile) return;
        
        console.log('📸 [MyAccountPage] Arquivo selecionado:', {
            name: selectedFile.name,
            type: selectedFile.type,
            size: selectedFile.size,
            isValidFile: selectedFile instanceof File
        });
        
        // Validação de tipo de arquivo
        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
        if (!allowedTypes.includes(selectedFile.type)) {
            setUploadError('Tipo de arquivo não suportado. Use JPG, PNG, WEBP ou GIF.');
            return;
        }
        
        // Validação de tamanho (5MB)
        if (selectedFile.size > 5 * 1024 * 1024) {
            setUploadError('A imagem deve ter no máximo 5MB.');
            return;
        }

        setUploadError(null);
        setPhotoFile(selectedFile);
        
        const previewUrl = URL.createObjectURL(selectedFile);
        setPhotoPreview(previewUrl);
        
        // Limpar o input para permitir selecionar o mesmo arquivo novamente
        event.target.value = '';
    };

    // ✅ Handle para enviar foto
    const handleUploadPhoto = async () => {
        console.log('📤 [MyAccountPage] Iniciando upload da foto pessoal', { 
            photoFile: photoFile ? {
                name: photoFile.name,
                type: photoFile.type,
                size: photoFile.size,
                isFile: photoFile instanceof File,
                isBlob: photoFile instanceof Blob
            } : 'null'
        });
        
        if (!photoFile) {
            console.error('❌ [MyAccountPage] Nenhum arquivo selecionado para upload');
            setUploadError('Selecione um arquivo primeiro.');
            setAlert('Selecione uma foto antes de enviar.', 'warning');
            return;
        }
        
        // ✅ VERIFICAÇÃO CRÍTICA ADICIONAL
        if (!(photoFile instanceof File) && !(photoFile instanceof Blob)) {
            console.error('❌ [MyAccountPage] Tipo de arquivo inválido:', typeof photoFile);
            setUploadError('Arquivo inválido. Selecione uma imagem novamente.');
            return;
        }
        
        // ✅ VERIFICAÇÃO CRÍTICA: Verifique se a mutation existe
        if (!updateMyPhoto || typeof updateMyPhoto.mutateAsync !== 'function') {
            console.error('❌ [MyAccountPage] updateMyPhoto não está disponível:', updateMyPhoto);
            setUploadError('Função de upload não disponível no momento.');
            setAlert('Erro de configuração: Função de upload não disponível', 'error');
            return;
        }
        
        try {
            const formDataObj = new FormData();
            console.log('📦 [MyAccountPage] Criando FormData...');
            
            // ✅ APENAS UM CAMPO - 'photo'
            formDataObj.append('photo', photoFile);
            
            console.log('📦 [MyAccountPage] FormData criado:', {
                hasPhoto: formDataObj.has('photo'),
                formDataKeys: Array.from(formDataObj.keys()),
                fileInfo: formDataObj.get('photo') ? {
                    name: formDataObj.get('photo').name,
                    type: formDataObj.get('photo').type,
                    size: formDataObj.get('photo').size
                } : 'null'
            });
            
            console.log('🚀 [MyAccountPage] Iniciando mutation updateMyPhoto...');
            
            // ✅ Usar a mutation corretamente
            const result = await updateMyPhoto.mutateAsync(formDataObj);
            
            console.log('✅ [MyAccountPage] Foto enviada com sucesso!', result);
            setAlert('Sua foto foi atualizada com sucesso!', 'success');
            
            // Limpar estados locais
            setPhotoFile(null);
            setUploadError(null);
            
            // Forçar refetch para atualizar cache e interface
            await refetch();
            
            // Liberar URL do preview
            if (photoPreview && photoPreview.startsWith('blob:')) {
                URL.revokeObjectURL(photoPreview);
            }
            setPhotoPreview(null);
            
        } catch (error) {
            console.error('❌ [MyAccountPage] Erro no upload da foto:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status,
                config: error.config
            });
            
            let errorMessage = 'Erro ao enviar foto';
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            // Tratamento específico de erros
            if (error.message.includes('FormData')) {
                errorMessage = 'Erro no formato do arquivo. Tente selecionar a imagem novamente.';
            } else if (error.message.includes('400') || error.message.includes('Bad Request')) {
                if (error.message.includes('Nenhum arquivo enviado')) {
                    errorMessage = 'Nenhum arquivo foi enviado. Selecione uma imagem primeiro.';
                } else if (error.message.includes('Too many files')) {
                    errorMessage = 'Muitos arquivos enviados. O sistema espera apenas um arquivo por vez.';
                } else {
                    errorMessage = 'Formato de arquivo inválido ou muito grande (máx. 5MB)';
                }
            } else if (error.message.includes('413')) {
                errorMessage = 'Arquivo muito grande (máximo 5MB)';
            } else if (error.message.includes('415')) {
                errorMessage = 'Tipo de arquivo não suportado (use JPG, PNG, WEBP ou GIF)';
            } else if (error.code === 'LIMIT_FILE_COUNT') {
                errorMessage = 'Muitos arquivos enviados. Envie apenas uma imagem por vez.';
            }
            
            setUploadError(errorMessage);
            setAlert(errorMessage, 'error');
        }
    };

    // ✅ Handle para remover foto
    const handleRemovePhoto = async () => {
        if (!window.confirm('Tem certeza que deseja remover sua foto de perfil?')) {
            return;
        }
        
        console.log('🗑️ [MyAccountPage] Iniciando remoção da foto pessoal');
        
        // ✅ VERIFICAÇÃO CRÍTICA: Verifique se a mutation existe
        if (!removeMyPhoto || typeof removeMyPhoto.mutateAsync !== 'function') {
            console.error('❌ [MyAccountPage] removeMyPhoto não está disponível:', removeMyPhoto);
            setAlert('Função de remover foto não disponível no momento.', 'error');
            return;
        }
        
        try {
            await removeMyPhoto.mutateAsync();
            setAlert('Sua foto foi removida com sucesso!', 'success');
            
            // Limpar preview
            if (photoPreview && photoPreview.startsWith('blob:')) {
                URL.revokeObjectURL(photoPreview);
            }
            setPhotoPreview(null);
            setPhotoFile(null);
            setUploadError(null);
            
            // Forçar refetch
            await refetch();
            
        } catch (error) {
            console.error('❌ [MyAccountPage] Erro ao remover foto:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
            
            let errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Erro ao remover foto';
            setAlert(errorMessage, 'error');
        }
    };

    // ✅ Cancelar upload/seleção de foto
    const handleCancelPhoto = () => {
        console.log('❌ [MyAccountPage] Cancelando upload/seleção de foto');
        
        if (photoPreview && photoPreview.startsWith('blob:')) {
            URL.revokeObjectURL(photoPreview);
        }
        setPhotoPreview(null);
        setPhotoFile(null);
        setUploadError(null);
        
        // Limpar input de arquivo
        const input = document.getElementById('myAccountPhotoInput');
        if (input) {
            input.value = '';
        }
    };

    const triggerFileInput = () => {
        console.log('📸 [MyAccountPage] Acionando input de arquivo');
        const input = document.getElementById('myAccountPhotoInput');
        if (input) {
            input.click();
        }
    };

    // --- HANDLERS DE FORMULÁRIO ---
    const handleFormChange = (event) => {
        const { name, value, type, checked } = event.target;
        if (name === 'nestedUpdate') { setFormData(value); return; }
        if (name === 'photoFile') return;
        const finalValue = type === 'checkbox' ? checked : value;
        setFormData(prev => ({ ...prev, [name]: finalValue }));
    };

    // Aceita (field, value) — formato usado por ProfileSection e PasswordSection
    const handleFieldChange = useCallback((field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    }, []);

    const handleUnitsChange = useCallback((unidades, comissoes) => {
        setSelectedUnidades(unidades);
        setComissoesConfig(prev => ({ ...prev, unidades: comissoes || {} }));
    }, []);

    const handleServicesChange = useCallback((servicos, comissoes) => {
        setSelectedServicos(servicos);
        setComissoesConfig(prev => ({ ...prev, servicos: comissoes || {} }));
    }, []);

    // --- AÇÕES DE SALVAR ---
    const handleSaveProfile = async () => {
        setIsSaving(true);
        try {
            // Enviar apenas campos simples que o backend aceita
            const dataToSend = {
                name: formData.name,
                email: formData.email,
                telefone: formData.telefone,
                cpf: formData.cpf,
                dataNascimento: formData.dataNascimento,
            };
            await updateMyDetails.mutateAsync(dataToSend);
            await refetch();
            setAlert('Perfil atualizado com sucesso!', 'success');
        } catch (error) {
            setAlert(error.response?.data?.error || error.message || 'Erro ao atualizar perfil', 'danger');
        } finally {
            setIsSaving(false);
        }
    };

    const handleSavePassword = async () => {
        if (!formData.currentPassword) {
            setAlert('Informe sua senha atual', 'warning');
            return;
        }
        if (!formData.password || formData.password.length < 6) {
            setAlert('A senha deve ter no mínimo 6 caracteres', 'warning');
            return;
        }
        if (formData.password !== formData.confirmPassword) {
            setAlert('As senhas não coincidem', 'warning');
            return;
        }

        setIsSaving(true);
        try {
            await updateMyPassword.mutateAsync({
                currentPassword: formData.currentPassword,
                newPassword: formData.password,
            });

            setFormData(prev => ({ ...prev, password: '', confirmPassword: '', currentPassword: '' }));
            setAlert('Senha atualizada com sucesso!', 'success');
        } catch (error) {
            setAlert(error.message || 'Erro ao atualizar senha', 'danger');
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveVinculos = async () => {
        setIsSaving(true);
        try {
            await updateMyDetails.mutateAsync({
                unidades: selectedUnidades,
                servicosHabilitados: selectedServicos,
                comissoesConfig,
            });
            setAlert('Configurações de atendimento salvas!', 'success');
        } catch (error) {
            setAlert(error.response?.data?.error || error.message || 'Erro ao salvar configurações', 'danger');
        } finally {
            setIsSaving(false);
        }
    };

    // ✅ Renderiza botão de ação dinamicamente
    const renderSaveButton = () => {
        // Se houver foto para upload na aba perfil
        if (activeTab === 'perfil' && photoFile) {
            // ✅ VERIFICAR SE O ARQUIVO É VÁLIDO
            const isValidFile = photoFile && 
                              (photoFile instanceof File || photoFile instanceof Blob) && 
                              photoFile.size > 0;
            
            console.log('🔄 [MyAccountPage] Renderizando botões de foto:', {
                hasPhotoFile: !!photoFile,
                isValidFile,
                isUploadingMyPhoto
            });
            
            return (
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={handleCancelPhoto}
                        className="action-button action-button-secondary"
                        disabled={isUploadingMyPhoto}
                    >
                        <X size={16} className="icon" />
                        Cancelar
                    </button>
                    <button
                        type="button"
                        onClick={handleUploadPhoto}
                        disabled={isUploadingMyPhoto || !isValidFile}
                        className={`action-button ${isValidFile ? 'action-button-primary' : 'action-button-disabled'}`}
                        title={!isValidFile ? 'Arquivo inválido. Selecione uma imagem válida.' : 'Enviar foto'}
                    >
                        {isUploadingMyPhoto ? (
                            <>
                                <Loader2 size={16} className="icon animate-spin" />
                                Enviando...
                            </>
                        ) : (
                            <>
                                <UploadCloud size={16} className="icon" />
                                {isValidFile ? 'Enviar Foto' : 'Arquivo Inválido'}
                            </>
                        )}
                    </button>
                </div>
            );
        }

        return null;
    };

    const getSaveConfig = () => {
        switch (activeTab) {
            case 'perfil':
                return { onClick: handleSaveProfile, label: 'Salvar Perfil' };
            case 'senha':
                return { onClick: handleSavePassword, label: 'Atualizar Senha' };
            case 'unidades':
            case 'servicos':
                return { onClick: handleSaveVinculos, label: 'Salvar Configurações' };
            default:
                return null;
        }
    };
    const saveConfig = !(activeTab === 'perfil' && photoFile) ? getSaveConfig() : null;


    if (authLoading || !user) {
        return (
            <div className="flex flex-col items-center justify-center h-screen bg-gray-50">
                <Loader2 className="animate-spin text-primary mb-4" size={40} />
                <span className="text-gray-600 font-medium">Carregando sua conta...</span>
            </div>
        );
    }

    const hasAccount = user.account && user.account !== null;
    const showProfessionalTabs = user.profissional?.isProfissional && hasAccount;
    const contaId = hasAccount 
        ? (typeof user.account === 'object' ? user.account._id : user.account) 
        : null;

    return (
        <div className="page-management-container">
            <PageHeader
                icon={<UserIcon />}
                title="Minha Conta"
                subtitle={user.name}
                actions={renderSaveButton()}
            />

            {saveConfig && (
                <FabSaveButton
                    onClick={saveConfig.onClick}
                    label={saveConfig.label}
                    loading={isSaving}
                    disabled={isUploadingMyPhoto || isRemovingMyPhoto}
                />
            )}

            {/* Foto de perfil */}
            <div className="content-panel" style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', padding: '1rem 1.5rem' }}>
                <div className="relative" style={{ flexShrink: 0 }}>
                    <div style={{ width: 64, height: 64, borderRadius: '50%', overflow: 'hidden', border: '3px solid var(--color-border)', background: 'var(--color-bg-alt)', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {photoPreview || user.photoUrl || user.photo ? (
                            <img
                                src={photoPreview || user.photoUrl || user.photo}
                                alt={user.name}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                            />
                        ) : (
                            <span style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-primary)' }}>
                                {user.name?.substring(0, 2).toUpperCase() || 'US'}
                            </span>
                        )}
                        {(isUploadingMyPhoto || isRemovingMyPhoto) && (
                            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Loader2 className="animate-spin" size={20} style={{ color: '#fff' }} />
                            </div>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={triggerFileInput}
                        disabled={isUploadingMyPhoto || isRemovingMyPhoto}
                        title="Alterar foto"
                        style={{ position: 'absolute', bottom: 0, right: 0, width: 24, height: 24, borderRadius: '50%', background: 'var(--color-primary)', color: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                        <Camera size={13} />
                    </button>
                    <input
                        type="file"
                        id="myAccountPhotoInput"
                        accept="image/*"
                        onChange={handlePhotoFileChange}
                        style={{ display: 'none' }}
                        disabled={isUploadingMyPhoto || isRemovingMyPhoto}
                    />
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.9375rem' }}>{user.name}</div>
                    <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 2 }}>
                        {user.email}
                        {user.role?.name && <span style={{ marginLeft: '0.5rem' }} className="badge badge-info">{user.role.name}</span>}
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0 }}>
                    {photoFile && (
                        <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                            {photoFile.name} ({(photoFile.size / 1024 / 1024).toFixed(1)} MB)
                        </span>
                    )}
                    {uploadError && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.8125rem', color: 'var(--color-danger)' }}>
                            <AlertCircle size={14} /> {uploadError}
                        </div>
                    )}
                    {(user.photoUrl || user.photo) && !photoFile && (
                        <button
                            type="button"
                            onClick={handleRemovePhoto}
                            disabled={isRemovingMyPhoto}
                            className="action-button action-button-secondary"
                            style={{ fontSize: '0.8125rem' }}
                        >
                            {isRemovingMyPhoto ? <Loader2 size={14} className="animate-spin icon" /> : <X size={14} className="icon" />}
                            Remover foto
                        </button>
                    )}
                </div>
            </div>

            {/* Abas */}
            <div className="form-tabs-nav" style={{ marginTop: '1rem' }}>
                <button type="button" className={`tab-button ${activeTab === 'perfil' ? 'is-active' : ''}`} onClick={() => setActiveTab('perfil')} disabled={isSaving || isUploadingMyPhoto || isRemovingMyPhoto}>
                    <UserIcon size={16} className="icon" /> Dados Pessoais
                </button>
                {showProfessionalTabs && (
                    <>
                        <button type="button" className={`tab-button ${activeTab === 'unidades' ? 'is-active' : ''}`} onClick={() => setActiveTab('unidades')} disabled={isSaving || isUploadingMyPhoto || isRemovingMyPhoto}>
                            <MapPin size={16} className="icon" /> Unidades
                        </button>
                        <button type="button" className={`tab-button ${activeTab === 'servicos' ? 'is-active' : ''}`} onClick={() => setActiveTab('servicos')} disabled={isSaving || isUploadingMyPhoto || isRemovingMyPhoto}>
                            <Settings size={16} className="icon" /> Serviços
                        </button>
                    </>
                )}
                <button type="button" className={`tab-button ${activeTab === 'senha' ? 'is-active' : ''}`} onClick={() => setActiveTab('senha')} disabled={isSaving || isUploadingMyPhoto || isRemovingMyPhoto}>
                    <Lock size={16} className="icon" /> Segurança
                </button>
            </div>

            {/* Conteúdo das Abas */}
            <div className="content-panel" style={{ minHeight: 400 }}>
                {activeTab === 'perfil' && (
                    <ProfileSection
                        user={formData}
                        onChange={handleFieldChange}
                        contextData={{
                            contaId: contaId,
                            allowedRoles: ['conta_admin', 'profissional', 'secretaria'],
                            defaultRole: 'profissional',
                            isOwnProfile: true
                        }}
                        onUpdateSuccess={refetch}
                    />
                )}
                {activeTab === 'unidades' && showProfessionalTabs && (
                    <UnitsSection user={user} contextData={{ contaId: contaId }} />
                )}
                {activeTab === 'servicos' && showProfessionalTabs && (
                    <ServicesSection user={user} contextData={{ contaId: contaId }} />
                )}
                {activeTab === 'senha' && (
                    <PasswordSection user={formData} isSelfService onChange={handleFieldChange} />
                )}
            </div>
        </div>
    );
};

export default MyAccountPage;